/* eslint-disable @typescript-eslint/no-unused-vars */
import  { ChangeEvent, FC, useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { notificationService } from "../../../shared/services/notification-service";
import { useAuth } from "../../../shared/authentications/auth-context";
import { SystemLogService } from "../../../shared/services/system-log-service";
import * as XLSX from "xlsx"; // Import XLSX for reading Excel files
import VacationCard, {
  VacationCardModel,
} from "../../cores/homes/components/vacation-card";
import YearInput from "../../../shared/components/atoms/year-input";
import { useNavigate } from "react-router-dom";
import { apiHolidaysService } from "../../../shared/services/holidays-service"; // You'll need to create this service


interface FormData {
  year: Date;
  excelFile: File | null
}

const SetHolidaysPage: FC = () => {
 
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [excelVacationData, setExcelVacationData] = useState<VacationCardModel[]>([]);
  const { authJwt } = useAuth();
  const [formData, setFormData] = useState<FormData>({
    year: new Date(),
    excelFile: null,
  });
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const createSystemLog = async (data: any) => {
    try {
      await SystemLogService.create(data);
    } catch (error) {
      const err = error as any;
      notificationService.dangerNotification("ระบบขัดข้อง", err.message);
    }
  };

  useEffect(() => {
    createSystemLog({
      employee_id: authJwt?.employee_id,
      module: "set-holidays",
      action: "set-holidays",
    });
  }, [authJwt?.employee_id]);

  const handleYearChange = (date: Date) => {
    setFormData(prev => ({ ...prev, year: date }));
  };

  const handleExcelFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (fileList && fileList.length > 0) {
      const file = fileList[0];
      setFormData(prev => ({ ...prev, excelFile: file }));

      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false });

        const vacationData = jsonData.map((item: any) => {
          const thaiDate = item["วันที่"];
          const [day, month, _] = thaiDate.split("/").map(Number);
          // Use the selected year from formData instead of converting from BE
          const selectedYear = formData.year.getFullYear();
          const date = new Date(selectedYear, month - 1, day);

          const monthName = date.toLocaleString("th-TH", { month: "long" });
          const dayName = item["วันในสัปดาห์"];
          const dayNumber = date.getDate().toString();
          return {
            year: selectedYear.toString(),
            month: monthName,
            vacations: [
              {
                day_number: dayNumber,
                day_name: dayName,
                description: item["วันหยุด"],
              },
            ],
          };
        });

        console.log(vacationData);
        setExcelVacationData(vacationData);
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof FormData, string>> = {};
    
    if (!formData.year) {
      errors.year = "กรุณาเลือกปีเริ่มใช้งาน";
    }
    if (!formData.excelFile) {
      errors.excelFile = "กรุณาเลือกไฟล์ Excel";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        setLoading(true);
        
        // Create FormData object to send file
        const formDataToSend = new FormData();
        formDataToSend.append('year', (formData.year?.getFullYear() + 543).toString());
        if (formData.excelFile) {
          formDataToSend.append('file', formData.excelFile);
        }

        const response = await apiHolidaysService.uploadHolidays(formDataToSend);
        
        if (response) {
          notificationService.successNotification("บันทึกข้อมูลเรียบร้อยแล้ว", "");
          navigate("/holidays/list");
        }
      } catch (error) {
        const err = error as any;
        if (err.response?.status === 400) {
          notificationService.dangerNotification(
            "ไม่สามารถบันทึกข้อมูลได้",
            err.response.data.message
          );
        } else {
          notificationService.dangerNotification("ระบบขัดข้อง", err.message);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between py-4">
        <h2 className="text-mof-primary m-0">ตั้งค่าวันหยุดประจำปี</h2>
      </div>

      <Form noValidate onSubmit={handleSubmit}>
        <div className="row mt-3">
          <div className="col-sm-12 col-md-4">
            <div className={formErrors.year ? 'is-invalid' : ''}>
              <label className="form-label">ปีเริ่มใช้งาน</label>
              <YearInput 
                date={formData.year}
                onChange={(date: any) => handleYearChange(date || new Date())}
                required={true}
              />
              {formErrors.year && (
                <div className="invalid-feedback">
                  {formErrors.year}
                </div>
              )}
            </div>
          </div>

          <div className="col-sm-12 col-md-4 mt-sm-3 mt-md-0">
            <div className={formErrors.excelFile ? 'is-invalid' : ''}>
              <label className="form-label">เลือกไฟล์ Excel</label>
              <input
                type="file"
                accept=".xlsx, .xls"
                onChange={handleExcelFileChange}
                className={`form-control ${formErrors.excelFile ? 'is-invalid' : ''}`}
              />
              {formErrors.excelFile && (
                <div className="invalid-feedback">
                  {formErrors.excelFile}
                </div>
              )}
            </div>
          </div>
        </div>

        {excelVacationData.length > 0 && (
          <div className="row mt-4 mb-5">
            <div className="col-sm-12 col-md-12 px-1">
              <div className="card shadow px-3 py-3">
                <h4 className="text-success mb-2">
                  วันหยุดประจำปี{" "}
                  {formData.year?.getFullYear()
                    ? formData.year?.getFullYear() + 543
                    : ""}
                </h4>

                {excelVacationData.map((data, index) => {
                  return <VacationCard key={index} {...data} />;
                })}
              </div>
            </div>
          </div>
        )}

        <div className="row mt-4 mb-5">
          <div className="col-sm-8 col-md-8 col-lg-10"></div>
          <div className="col-sm-4 col-md-4 col-lg-2">
            <Button
              variant="primary"
              type="submit"
              className="d-block btn-action"
              style={{ background: "#007BFF" }}
              disabled={excelVacationData.length === 0 || loading}
            >
              {loading ? "กำลังบันทึก..." : "สร้าง"}
            </Button>
          </div>
        </div>
      </Form>
    </>
  );
};

export default SetHolidaysPage;
